"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useUniversalAuth } from "@wtc/ui";

import type {
  DashboardSummaryPayload,
  DashboardSummaryResponse,
} from "./dashboard-summary-types";

export const DASHBOARD_SUMMARY_QUERY_KEY = "dashboard-summary";

/** Matches the server cache TTL (5 min) so the client doesn't refetch more often than the cache changes. */
const DASHBOARD_SUMMARY_STALE_MS = 5 * 60 * 1000;

const EMPTY_SUMMARY: DashboardSummaryPayload = {
  stats: null,
  criticalTickets: null,
  recentActivity: null,
  timeSummary: null,
  taskStats: null,
};

/**
 * Single cached fetch of every dashboard section.
 *
 * All five dashboard hooks read from this one query (same `queryKey`), so a
 * dashboard page that previously fired five browser Apollo queries now makes ONE
 * request to the server-cached `/api/dashboard/summary` route. Processing of the
 * raw payload stays in each section hook, unchanged.
 */
export function useDashboardSummary() {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const { getAuthToken } = useAuth();
  const userId = appUser?.id;

  const query = useQuery<DashboardSummaryPayload>({
    queryKey: [DASHBOARD_SUMMARY_QUERY_KEY, userId],
    enabled: Boolean(isAuthenticated && userId),
    staleTime: DASHBOARD_SUMMARY_STALE_MS,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const token = await getAuthToken();
      const headers = new Headers({ "Content-Type": "application/json" });
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch("/api/dashboard/summary", {
        headers,
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`Failed to load dashboard summary (${res.status})`);
      const body = (await res.json()) as DashboardSummaryResponse;
      return body.data;
    },
  });

  return {
    data: query.data ?? EMPTY_SUMMARY,
    // v5 `isLoading` = first load with no data; stays false during background
    // refetch, so a populated dashboard never blanks (MOUNT_GATE_MUST_NOT_HIDE_DATA).
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}

/**
 * Clear-on-write: tell the server to drop this user's cached dashboard, then
 * refetch the client query so the dashboard reflects the change immediately.
 * Call after a mutation that affects dashboard data (time logged, task/ticket change).
 * Best-effort — a failed invalidation never throws.
 */
export function useBustDashboardCache() {
  const { getAuthToken } = useAuth();
  const { appUser } = useUniversalAuth();
  const queryClient = useQueryClient();
  const userId = appUser?.id;

  return useCallback(async () => {
    try {
      const token = await getAuthToken();
      const headers = new Headers({ "Content-Type": "application/json" });
      if (token) headers.set("Authorization", `Bearer ${token}`);
      await fetch("/api/dashboard/cache/invalidate", {
        method: "POST",
        headers,
        credentials: "same-origin",
      });
    } catch {
      // best-effort — the server cache still expires on its own within the TTL
    } finally {
      await queryClient.invalidateQueries({ queryKey: [DASHBOARD_SUMMARY_QUERY_KEY, userId] });
    }
  }, [getAuthToken, queryClient, userId]);
}
